/**
 * Trims populated Media docs down to what the frontend Media components read.
 *
 * Props handed to a client component are serialised into the page's inline RSC
 * payload. A full Media doc carries SEO bookkeeping (tags, usage, alt-text
 * review fields) and every rendition's filesize/mime/filename — on the homepage
 * the logo carousels alone added ~75 KB of that to the HTML.
 *
 * Non-object values (unpopulated ids, null) pass through untouched.
 */
const MEDIA_FIELDS = [
  'id',
  'alt',
  'title',
  'description',
  'caption',
  'url',
  'filename',
  'mimeType',
  'filesize',
  'width',
  'height',
  'focalX',
  'focalY',
  'updatedAt',
  'createdAt',
  'videoThumbnailUrl',
] as const

// Renditions ImageMedia can swap in (see SWAPPABLE_SIZES there).
const RENDITIONS = ['thumbnail', 'small', 'medium', 'large', 'xlarge'] as const

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyRecord = Record<string, any>

const isMediaDoc = (value: AnyRecord): boolean =>
  typeof value.mimeType === 'string' && typeof value.filename === 'string' && 'url' in value

// Lexical editor state: its upload nodes are rendered by Payload's own
// converters, which read rendition fields this would otherwise drop.
const isEditorState = (value: AnyRecord): boolean =>
  Boolean(value.root && typeof value.root === 'object' && 'children' in value.root)

const slimSizes = (sizes: unknown): AnyRecord | undefined => {
  if (!sizes || typeof sizes !== 'object') return undefined

  const source = sizes as AnyRecord
  const out: AnyRecord = {}
  for (const name of RENDITIONS) {
    const rendition = source[name]
    if (rendition?.url) {
      out[name] = { url: rendition.url, width: rendition.width, height: rendition.height }
    }
  }
  return out
}

export const slimMedia = <T>(media: T): T => {
  if (!media || typeof media !== 'object') return media

  const source = media as AnyRecord
  const slim: AnyRecord = {}

  for (const field of MEDIA_FIELDS) {
    if (source[field] !== undefined) slim[field] = source[field]
  }

  if (source.sizes !== undefined) slim.sizes = slimSizes(source.sizes)

  // Video posters are Media docs themselves. Their renditions are kept: a
  // `poster` attribute takes one URL, so VideoMedia picks a rendition rather
  // than serving the full-size frame.
  if (source.videoThumbnail !== undefined) {
    const poster = source.videoThumbnail
    slim.videoThumbnail =
      poster && typeof poster === 'object'
        ? {
            url: poster.url,
            width: poster.width,
            height: poster.height,
            sizes: slimSizes(poster.sizes),
          }
        : poster
  }

  return slim as T
}

/**
 * Walks block data and slims every Media doc inside it. All object keys are
 * preserved (blocks sniff shapes like `'logo' in entry`); only the Media docs
 * themselves shrink.
 */
export const slimMediaDeep = <T>(value: T, depth = 0): T => {
  if (!value || typeof value !== 'object' || depth > 12) return value
  if (Array.isArray(value)) return value.map((item) => slimMediaDeep(item, depth + 1)) as T

  // Rebuilding anything that isn't a plain object would destroy it: a Date has
  // no enumerable own properties, so it would come back as {}.
  const proto = Object.getPrototypeOf(value)
  if (proto !== Object.prototype && proto !== null) return value

  const record = value as AnyRecord
  if (isMediaDoc(record)) return slimMedia(record) as T
  if (isEditorState(record)) return value

  const out: AnyRecord = {}
  for (const [key, child] of Object.entries(record)) out[key] = slimMediaDeep(child, depth + 1)
  return out as T
}
